import React, { useState } from 'react';
import {
  Button,
  Content,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Label,
  Popover,
  TextInput,
  Tooltip,
} from '@patternfly/react-core';
import { UndoIcon } from '@patternfly/react-icons';
import { DECIMAL_RE, formatPrice } from '../../../utils/money';

export interface CellMutationResult {
  ok: boolean;
  /** Backend field-level error (data.unit_price), surfaced inline; null for non-field failures. */
  fieldError: string | null;
}

interface DomainPriceCellProps {
  /** Unique within the table — used for the input id and popover focus target. */
  cellKey: string;
  /** Current effective price, or null when unpriced. */
  unitPrice: string | null;
  /** Client tab: true when this cell is a client override (shows a "Custom" badge). */
  isCustom?: boolean;
  /** When false, the cell is read-only (no editor). */
  editable: boolean;
  /** Describes the cell for aria-labels, e.g. "Renewal for .com". */
  ariaContext: string;
  /** The operation's line-item description, shown as context in the editor popover. */
  description?: string;
  onSave: (unitPrice: string) => Promise<CellMutationResult>;
  /** Clear the cell: "Clear" (default matrix) or "Reset to default" (client overrides). */
  onClear?: () => Promise<{ ok: boolean }>;
  clearLabel?: string;
  /** Whether the clear action is meaningful (a value exists / it's a custom override). */
  canClear?: boolean;
}

function DomainPriceCell({
  cellKey,
  unitPrice,
  isCustom = false,
  editable,
  ariaContext,
  description,
  onSave,
  onClear,
  clearLabel = 'Clear',
  canClear = false,
}: DomainPriceCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const trimmed = draft.trim();
  const isDraftValid = DECIMAL_RE.test(trimmed);
  const helperError =
    serverError ??
    (trimmed !== '' && !isDraftValid ? 'Enter a non-negative amount with up to 4 decimal places.' : null);

  const inputId = `domain-price-input-${cellKey}`;
  const priceText = unitPrice !== null ? formatPrice(unitPrice) : 'Set';
  // Fold the current value into the trigger's accessible name so screen-reader users hear the price,
  // not just the action (a bare "Edit price" aria-label would otherwise shadow the visible price text).
  const triggerAriaLabel =
    unitPrice !== null ? `Edit price for ${ariaContext}, currently ${priceText}` : `Set price for ${ariaContext}`;

  const openEditor = () => {
    if (isEditing) {
      return;
    }
    setDraft(unitPrice ?? '');
    setServerError(null);
    setIsEditing(true);
  };

  const closeEditor = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!isDraftValid) {
      return;
    }
    setIsSaving(true);
    setServerError(null);
    const result = await onSave(trimmed);
    setIsSaving(false);
    if (result.ok) {
      setIsEditing(false);
    } else {
      setServerError(result.fieldError);
    }
  };

  const handleClear = async () => {
    if (!onClear) {
      return;
    }
    setIsClearing(true);
    const result = await onClear();
    setIsClearing(false);
    if (result.ok) {
      setIsEditing(false);
    }
  };

  if (!editable) {
    return unitPrice !== null ? <>{priceText}</> : <Content component="small">—</Content>;
  }

  const editorBody = (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        void handleSave();
      }}
    >
      {description && (
        <div className="pf-v6-u-mb-sm">
          <Content component="small">{description}</Content>
        </div>
      )}
      <FormGroup label="Unit price (excl. VAT)" fieldId={inputId}>
        <TextInput
          id={inputId}
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={(_event, value) => {
            setDraft(value);
            if (serverError) {
              setServerError(null);
            }
          }}
          validated={helperError ? 'error' : 'default'}
          isDisabled={isSaving || isClearing}
          aria-label={`Unit price for ${ariaContext} in euros, excluding VAT`}
        />
        {helperError && (
          <FormHelperText>
            <HelperText>
              <HelperTextItem variant="error">{helperError}</HelperTextItem>
            </HelperText>
          </FormHelperText>
        )}
      </FormGroup>
      <Flex spaceItems={{ default: 'spaceItemsSm' }}>
        <FlexItem>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSaving}
            isDisabled={isSaving || isClearing || !isDraftValid}
          >
            Save
          </Button>
        </FlexItem>
        {canClear && onClear && (
          <FlexItem>
            <Button
              variant="secondary"
              isDanger
              onClick={() => void handleClear()}
              isLoading={isClearing}
              isDisabled={isSaving || isClearing}
            >
              {clearLabel}
            </Button>
          </FlexItem>
        )}
        <FlexItem>
          <Button variant="link" onClick={closeEditor} isDisabled={isSaving || isClearing}>
            Cancel
          </Button>
        </FlexItem>
      </Flex>
    </Form>
  );

  return (
    <>
      <Popover
        isVisible={isEditing}
        shouldClose={closeEditor}
        headerContent={`Set price — ${ariaContext}`}
        bodyContent={editorBody}
        aria-label={`Edit price for ${ariaContext}`}
        // CSS.escape so a multi-label TLD id (e.g. ...-co.uk) is matched by exact id, not parsed as
        // an id + class — otherwise the focus-trap selector finds nothing and the input isn't focused.
        elementToFocus={`#${CSS.escape(inputId)}`}
      >
        <Button variant="link" isInline onClick={openEditor} aria-label={triggerAriaLabel}>
          {priceText}
        </Button>
      </Popover>
      {isCustom && (
        <>
          <Label isCompact color="blue" className="pf-v6-u-ml-xs">
            Custom
          </Label>
          {canClear && onClear && (
            <Tooltip content="Reset to the platform default">
              <Button
                variant="plain"
                onClick={() => void handleClear()}
                isDisabled={isClearing}
                isLoading={isClearing}
                aria-label={`Reset ${ariaContext} to the platform default`}
                icon={<UndoIcon />}
                className="pf-v6-u-ml-xs"
              />
            </Tooltip>
          )}
        </>
      )}
    </>
  );
}

export default DomainPriceCell;
